import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { Button, Dialog, TextArea, Uploader } from '@app/components';

import { ActivityLogInfo } from '@app/types';

import {
  ModalContainer,
  ChecklistText,
  ChecklistInfoBox,
  Footer,
  ChecklistButtonContainer,
  CancelButton,
  SavingIndicator,
  ModalTextArea,
  ChecklistInfoCard,
} from '@app/modules/transaction-details/transactions-details-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '@app/styles';
import TransactionlogoUrl from '@app/assets/images/Transactionblack.svg';
import { useUpdateChecklistMutation } from '@app/graphql';

type inspProps = {
  inspectionNotes?: string;
  image?: string;
  fileName?: string;
  userType?: string;
};

type Props = {
  onModalClose?: any;
  userFirebaseId: string;
  instructions?: string;
  docs?: string;
  data: inspProps;
  status?: string;
  loadChecklistData: (checklistData: any, pendingTasks: string) => void;
  getActivityLogData: (e: ActivityLogInfo[]) => void;
};
let activityLog: ActivityLogInfo[] = [];

export default function InspectionModal({
  onModalClose,
  userFirebaseId,
  instructions,
  docs,
  loadChecklistData,
  data,
  status,
  getActivityLogData,
}: Props) {
  const { register } = useForm();
  const { transactionId }: any = useParams();
  const [updateCheckList] = useUpdateChecklistMutation();
  const [fileName, setFileName] = React.useState<string>('');
  const [saving, updatesaving] = React.useState<boolean>(false);
  const [inspectionData, setInspectionData] = React.useState({
    notes: '',
    image: TransactionlogoUrl,
  });

  React.useEffect(() => {
    if (data) {
      activityLog = [];
      const { image = '', fileName = '', inspectionNotes = '' } = data;
      setInspectionData({
        notes: inspectionNotes,
        image: image ? image : TransactionlogoUrl,
      });
      setFileName(fileName);
    }
  }, [data]);

  const onImageChange = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      setFileName(event.target.files[0].name);
      const reader = new FileReader();
      reader.onload = (e: any) => {
        setInspectionData({
          ...inspectionData,
          image: e.target.result,
        });
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  };
  const deleteImage = () => {
    setInspectionData({
      ...inspectionData,
      image: TransactionlogoUrl,
    });
    setFileName('');
  };

  const getActivityLog = () => {
    if (status !== 'COMPLETED') {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userFirebaseId,
        item: `Response`,
        description: 'for Inspection task has been submitted by ',
      });
      return activityLog;
    } else {
      if (data.inspectionNotes !== inspectionData.notes) {
        activityLog.push({
          transactionId: transactionId,
          firebaseId: userFirebaseId,
          item: `Response`,
          description: 'for Inspection task has been updated by ',
        });
      }
      return activityLog;
    }
  };

  const submitChecklistData = () => {
    updatesaving(true);
    const checklistVariables = {
      transactionId: transactionId,
      status: 'COMPLETED',
      userType: data && data.userType ? data.userType : '',
      assignmentChecklistKey: 'inspection',
      completedAt: moment().format('YYYY-MM-DD'),
      firebaseId: userFirebaseId,
      inspectionNotes: inspectionData.notes,
      activityLog: getActivityLog(),
      inspectionDocuments: [
        {
          name: fileName,
          src:
            inspectionData.image === TransactionlogoUrl
              ? ''
              : inspectionData.image,
          uploadedDate: moment().format('YYYY-MM-DD'),
        },
      ],
    };
    updateCheckList({
      variables: checklistVariables,
    }).then((res: any) => {
      if (res.data.updateTransactionChecklist.success) {
        const {
          updateTransactionChecklist: { transaction },
        } = res.data;
        const { assignmentChecklist, pendingTasks, activityLog } = transaction;
        loadChecklistData(assignmentChecklist, pendingTasks);
        getActivityLogData(activityLog);
        updatesaving(false);
        setTimeout(onModalClose, 500);
      }
    });
  };

  return (
    <Dialog title="Inspection" onClose={onModalClose}>
      <ModalContainer>
        <ChecklistText>{instructions}</ChecklistText>
        <ChecklistInfoBox>
          <ModalTextArea>
            <TextArea
              label="Notes"
              placeholder="Enter Notes (any)"
              name="inspectionNotes"
              value={inspectionData.notes ? inspectionData.notes : ''}
              onChange={e => {
                setInspectionData({
                  ...inspectionData,
                  notes: e.target.value,
                });
              }}
              register={register({})}
            />
          </ModalTextArea>
          {docs === 'yes' && (
            <ChecklistInfoCard>
              <Uploader
                label=" Upload Inspection Document"
                fileName={fileName}
                profileImage={inspectionData.image}
                chooseText="Browse file"
                onChange={e => onImageChange(e)}
                borderRadius="8px"
                onImageDelete={deleteImage}
              />
            </ChecklistInfoCard>
          )}
        </ChecklistInfoBox>
      </ModalContainer>
      <Footer>
        {saving && (
          <SavingIndicator>
            <FontAwesomeIcon
              color={primaryColor}
              icon={faCircleNotch}
              size="lg"
              spin
            />
          </SavingIndicator>
        )}
        <CancelButton>
          <Button variant="text" size="medium" onClick={onModalClose}>
            Cancel
          </Button>
        </CancelButton>
        <ChecklistButtonContainer
          disabled={saving}
          onClick={submitChecklistData}>
          <Button variant="primary" size="medium" width="100%" type="submit">
            Submit Task
          </Button>
        </ChecklistButtonContainer>
      </Footer>
    </Dialog>
  );
}
